import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createCuisine, updateCuisine, fetchCuisineById } from './cuisineSlice';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import log from '../../logger';
import ReactForm from '../../components/ReactForm';
import { paths } from '../../routes/helpers';
import { getImageUrl, toLocalUploadPath } from '../../utils/urlUtils';
import ImageGallery from '../../components/ImageGallery';

const CuisineCreateEdit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cuisineId } = useParams();
  const loading = useSelector((state) => state.cuisine.loading);
  const error = useSelector((state) => state.cuisine.error);
  const cuisine = useSelector((state) => 
    cuisineId ? state.cuisine.items.find(b => b._id === cuisineId) : null
  );

  const [imageFiles, setImageFiles] = useState([]);
  const [existingImage, setExistingImage] = useState('');

  useEffect(() => {
    if (cuisineId) {
      dispatch(fetchCuisineById(cuisineId));
    }
  }, [cuisineId, dispatch]);

  useEffect(() => {
    if (cuisine && cuisine.image) {
      setExistingImage(getImageUrl(cuisine.image));
    }
  }, [cuisine]);

  const handleImageChange = (event) => {
    const files = Array.from(event.target.files);
    setImageFiles(files);
  };

  const handleRemoveImage = (index) => {
    setImageFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = () => {
    setExistingImage('');
  };

  const handleSubmit = async (formData) => {
    try {
      const processedData = new FormData();
      processedData.append('label', formData.label);
      processedData.append('status', parseInt(formData.status));
      
      if (imageFiles.length > 0) {
        processedData.append('image', imageFiles[0]);
      } else if (existingImage && cuisineId) {
        // Keep existing image
        processedData.append('existingImage', toLocalUploadPath(existingImage));
      }

      if (cuisineId) {
        await dispatch(updateCuisine({ id: cuisineId, formData: processedData })).unwrap();
        toast.success('Cuisine updated successfully!');
        log.info('Cuisine updated:', formData.label);
      } else {
        await dispatch(createCuisine(processedData)).unwrap();
        toast.success('Cuisine created successfully!');
        log.info('Cuisine created:', formData.label);
      }
      navigate(paths.private.cuisine.list());
    } catch (err) {
      toast.error(err.message || 'An error occurred');
      log.error('Error creating/updating cuisine:', err);
    }
  };

  const fields = [
    {
      name: 'label',
      label: 'Label',
      type: 'text',
      required: true,
      placeholder: 'Enter cuisine label'
    },
    {
      name: 'image',
      label: 'Image',
      type: 'file',
      accept: 'image/*',
      required: !cuisineId || (!existingImage && imageFiles.length === 0),
      onChange: handleImageChange,
      preview: (
        <ImageGallery 
          existingImages={imageFiles.length > 0 ? [] : existingImage ? [existingImage] : []}
          imageFiles={imageFiles}
          onRemoveExisting={handleRemoveExistingImage}
          onRemoveNew={handleRemoveImage}
          loading={loading && cuisine?.image && !existingImage}
        />
      )
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      defaultValue: "0",
      options: [
        { value: "0", label: 'Inactive' },
        { value: "1", label: 'Active' },
        { value: "2", label: 'Suspended' }
      ]
    }
  ];

  const defaultValues = cuisine ? {
    label: cuisine.label,
    image: [],
    status: cuisine.status.toString()
  } : {
    label: '',
    image: [],
    status: "1"
  };

  log.info('Default values:', defaultValues);

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">
        {cuisineId ? 'Edit Cuisine' : 'Create Cuisine'}
      </h2>
      <ReactForm
        fields={fields}
        onSubmit={handleSubmit}
        loading={loading}
        error={error}
        defaultValues={defaultValues}
        submitText={cuisineId ? 'Update Cuisine' : 'Create Cuisine'}
      />
    </div>
  );
};

export default CuisineCreateEdit; 